import React, { useState } from 'react';
import type { UserRole, Permission } from '../types';
import CheckCircleIcon from '../components/icons/CheckCircleIcon';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';

interface UserRolesPageProps {
  userRoles: UserRole[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => Promise<void>;
  onAddRole?: (role: Omit<UserRole, 'id'> & { id?: string }) => Promise<UserRole>;
  onUpdateRole?: (id: string, updates: Partial<Omit<UserRole, 'id'>>) => Promise<UserRole>;
  onDeleteRole?: (id: string) => Promise<void>;
}

const permissionDescriptions: Record<Permission, string> = {
  access_pos: 'Access the Point of Sale screen',
  access_reports: 'View sales and performance reports',
  manage_menu: 'Add, edit, and delete menu items',
  manage_inventory: 'Manage stock levels and inventory items',
  manage_staff: 'Add, edit, and remove staff members',
  manage_tables: 'Manage the restaurant floor plan and tables',
  manage_customers: 'View and manage the customer database',
  manage_loyalty: 'Configure the customer loyalty program',
  manage_purchasing: 'Create and manage purchase orders',
  manage_invoices: 'Create supplier and client tax invoices',
  manage_delivery: 'Manage delivery platform integrations',
  manage_settings: 'Access and change system-wide settings',
  view_staff_pins: 'View staff member PINs in staff management',
  view_staff_payroll: 'View staff pay rates and payroll details in staff management',
  test_integrations: 'Test third-party integrations',
  approve_integrations: 'Approve third-party integrations',
  view_integrations: 'View integration settings and status',
};

const allPermissions = Object.keys(permissionDescriptions) as Permission[];

const UserRolesPage: React.FC<UserRolesPageProps> = ({ 
  userRoles, 
  loading, 
  error, 
  onRefresh,
  onAddRole,
  onUpdateRole,
  onDeleteRole
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    permissions: [] as Permission[],
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const handleOpenModal = (role: UserRole | null = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        permissions: [...role.permissions],
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        permissions: [],
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
    setFormData({ name: '', permissions: [] });
    setFormErrors({});
  };

  const handlePermissionToggle = (permission: Permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (editingRole) {
        if (onUpdateRole) {
          await onUpdateRole(editingRole.id, {
            name: formData.name,
            permissions: formData.permissions,
          });
        }
      } else {
        if (onAddRole) {
          await onAddRole({
            name: formData.name,
            permissions: formData.permissions,
          });
        }
      }
      handleCloseModal();
    } catch (error: any) {
      setFormErrors({ _general: error.message || 'Failed to save role' });
    }
  };

  const handleDelete = async (roleId: string, roleName: string) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      if (onDeleteRole) {
        await onDeleteRole(roleId);
      }
    } catch (error: any) {
      alert(`Failed to delete role: ${error.message || 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="text-gray-500">Loading user roles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="text-red-500 mb-4">Error loading user roles: {error}</div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800">User Roles & Permissions</h1>
            <div className="flex items-center gap-4">
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm"
                >
                  Refresh
                </button>
              )}
              {onAddRole && (
                <button
                  onClick={() => handleOpenModal(null)}
                  className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 flex items-center gap-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  Add New Role
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="space-y-8">
          {userRoles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No user roles found.</p>
              {onAddRole && (
                <button
                  onClick={() => handleOpenModal(null)}
                  className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
                >
                  Create First Role
                </button>
              )}
            </div>
          ) : (
            userRoles.map((role) => (
              <div key={role.id} className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-800">{role.name}</h2>
                  <div className="flex items-center gap-2">
                    {onUpdateRole && (
                      <button
                        onClick={() => handleOpenModal(role)}
                        className="text-pink-600 hover:text-pink-900"
                        title="Edit role"
                      >
                        <EditIcon className="w-5 h-5" />
                      </button>
                    )}
                    {onDeleteRole && (
                      <button
                        onClick={() => handleDelete(role.id, role.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete role"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-md font-semibold text-gray-700 mb-4">Permissions:</h3>
                  <ul className="space-y-3">
                    {Object.entries(permissionDescriptions).map(([key, description]) => (
                      <li key={key} className="flex items-center">
                        <CheckCircleIcon 
                          className={`w-5 h-5 mr-3 ${role.permissions.includes(key as Permission) ? 'text-green-500' : 'text-gray-300'}`} 
                        />
                        <span className={`${role.permissions.includes(key as Permission) ? 'text-gray-800' : 'text-gray-400'}`}>
                          {description}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Add/Edit Role Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {editingRole ? 'Edit Role' : 'Add New Role'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="roleName" className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="roleName"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
                {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Permissions
                </label>
                <div className="border border-gray-300 rounded-md p-4 max-h-96 overflow-y-auto">
                  <div className="space-y-3">
                    {allPermissions.map((permission) => (
                      <label
                        key={permission}
                        className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission)}
                          onChange={() => handlePermissionToggle(permission)}
                          className="w-4 h-4 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          {permissionDescriptions[permission]}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {formErrors._general && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{formErrors._general}</p>
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
                >
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRolesPage;
